/**
 * C1 权限模型 · **前端门禁的唯一映射处**（Phase 3，2026-10-06）。
 *
 * ## 为什么单独一个文件
 *
 * 设计稿（`docs/plan/permission-model-design.md` §3.2）要求「feature ↔ 页面映射放**一处**」。
 * 本仓库为「两份清单分叉」栽过多次（ROAS、诊断码、口径），因此：
 *  · 页面 → 功能点的对应**只在这里**（`PAGE_FEATURES`）；导航过滤、路由守卫、页面内写控件
 *    全部走这里的函数；
 *  · 功能点清单本身**不复制**：直接用契约的 `FEATURE_POINTS`（唯一真相，前后端同一份）。
 *
 * ## 安全边界（务必记住）
 *
 * **前端隐藏 ≠ 安全**：这里只决定"看不看得到、按钮禁不禁用"（体验层）。真正的闸门在
 * 服务端中间件（Phase 2A/2B，fail-closed）。因此本文件的所有判定都只是**展示层**，
 * 绝不承担安全职责；绕过前端直接打 API 仍会被服务端拒。
 *
 * ## fail closed
 *
 *  · 未登记功能点 → `canView/canWrite` 都 false（契约 `accessOf` 的语义，本文件不重写）；
 *  · 权限数据**只从会话端点**读（`AuthUser.shopIds/features`），缺字段按契约 fail-closed
 *    处理（`shopIds=[]`、`features={}`）——**绝不**用"有没有数据"反推权限；
 *  · `role==='admin'` ⇒ 契约直接旁路（全可见可写），**不要求** grants。
 */

import {
  FEATURE_POINTS,
  USER_ROLES,
  featurePointOf,
  isSuperAdmin,
  type AccessLevel,
  type FeatureGrants,
  type FeatureKey,
  type UserAccess,
  type UserRole,
} from "@ams/contracts";
import type { AuthUser } from "./apiTypes.js";

/**
 * **页面路径 → 功能点**（唯一映射处）。
 *
 * ⚠️ 新增受权限约束的页面时**必须**在这里登记；漏登记 = 该页不被门禁（因为
 * `featureForPage` 返回 `null`）。不受功能权限约束的页面（`/login`、`/settings`）
 * **故意不登记**：设置页内部按 `settings_*` 面板各自判权，不是一个整页功能点。
 */
export const PAGE_FEATURES = {
  "/": "ad_metrics",
  "/overview": "overview",
  "/spend-overview": "spend_overview",
  "/products": "products",
  "/products/categories": "products",
  "/style-codes": "style_code_board",
  "/creatives": "creative_board",
  "/ad-management": "ad_management",
  "/google-campaigns": "google_campaigns",
  "/sync": "sync",
} as const satisfies Readonly<Record<string, FeatureKey>>;

/** 受门禁的页面路径联合（由映射表推导，**不手写第二份**）。 */
export type GuardedPagePath = keyof typeof PAGE_FEATURES;

/** 该路径的功能点；**未登记 ⇒ `null`**（不受整页门禁：登录页 / 设置页）。 */
export function featureForPage(path: string): FeatureKey | null {
  const normalized = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
  const feature = (PAGE_FEATURES as Readonly<Record<string, FeatureKey>>)[normalized];
  return feature ?? null;
}

/** 功能点的中文名（用于「权限不足：X」文案）；未登记 ⇒ 回落到 key 本身。 */
export function featureLabel(key: string): string {
  return featurePointOf(key)?.label ?? key;
}

/** 页面路径的中文名（「权限不足：X」优先用功能点名，与导航一致）。 */
export function featureLabelForPage(path: string): string | null {
  const feature = featureForPage(path);
  return feature === null ? null : featureLabel(feature);
}

// ---------------------------------------------------------------------------
// 会话 → 访问上下文（**只读会话数据，不推断**）
// ---------------------------------------------------------------------------

/**
 * 无会话时的**最低权限角色**（fail-closed）——把 `null` 会话降级成"什么都干不了"的 actor。
 *
 * ⚠️ 角色域唯一来源 = 契约 `USER_ROLES`（`packages/contracts/src/user-management.ts`，
 * 对应 DB CHECK `users_role_chk`）。**这里不写角色字面量**：从契约派生 ⇒ 域变了自动跟着变。
 *
 * 取法：契约按**权限由高到低**列出（`admin → manager → viewer`），故最低权限 = 最后一档；
 * 契约是非空 `as const` 元组，断言只是把"非空"带给类型系统。
 * 两层防线（`lib/minimalActorRole.spec.ts`）：① 它必须在 `USER_ROLES` 里；② 还必须落在
 * **迁移 0007 解析出的 DB 域**里（代码真值 vs DB 真值对撞，抓漂移）；③ 它不是超管角色。
 */
export const MINIMAL_ACTOR_ROLE: UserRole = USER_ROLES[USER_ROLES.length - 1] as UserRole;

/**
 * ⭐ **会话访问上下文** = 契约 `UserAccess` + **web 侧新增的 `featureAccess`**（服务端有效档位）。
 * ⚠️ 只加在 web 侧类型上（⛔ 不改契约）；判定函数读的就是它。
 */
export type SessionAccess = UserAccess & {
  readonly featureAccess?: readonly {
    readonly feature: string;
    readonly access: "none" | "read" | "write";
    readonly source: "override" | "role" | "super_admin";
  }[];
};

/** 有效档位清洗：只留**已登记**功能点 + 合法 access；`source` 原样保留（服务端事实）。 */
export function sanitizeFeatureAccess(raw: unknown): readonly {
  readonly feature: string;
  readonly access: "none" | "read" | "write";
  readonly source: "override" | "role" | "super_admin";
}[] {
  if (!Array.isArray(raw)) return [];
  const out: { feature: string; access: "none" | "read" | "write"; source: "override" | "role" | "super_admin" }[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) continue;
    const row = item as { feature?: unknown; access?: unknown; source?: unknown };
    if (typeof row.feature !== "string" || featurePointOf(row.feature) === undefined) continue;
    if (row.access !== "none" && row.access !== "read" && row.access !== "write") continue;
    if (row.source !== "override" && row.source !== "role" && row.source !== "super_admin") continue;
    out.push({ feature: row.feature, access: row.access, source: row.source });
  }
  return out;
}

/** 最低权限 actor（无会话 / 权限信息缺失时的 fail-closed 降级）。 */
export function minimalUserAccess(): SessionAccess {
  // ⭐ `featureAccess: []` ⇒ 一律不可用（fail closed，与"缺失 = 不可用"同一规则）。
  return { role: MINIMAL_ACTOR_ROLE, shopIds: [], features: {}, featureAccess: [] };
}

/**
 * 会话用户 → 契约 `UserAccess`。
 *
 * 只做**字段搬运**，不做任何推断：
 *  · `shopIds` 缺省 → `[]`（fail closed：看不到任何店铺，符合契约「无 grants ⇒ 空集」）；
 *  · `features` 缺省 → `{}`（缺键 = 无权限）；
 *  · `admin` 旁路由契约负责（本函数不特殊处理）。
 */
export function accessOfAuthUser(user: AuthUser | null): SessionAccess | null {
  if (user === null) return null;
  return {
    role: user.role,
    shopIds: sanitizeShopIds(user.shopIds),
    features: sanitizeFeatureGrants(user.features),
    // ⭐⭐ 2026-10-08（线上报障）：**必须把服务端的有效档位搬运过来** ——
    // 之前这里丢掉了它，于是在"有角色模板、零覆盖"的用户上所有功能判定都是 false。
    ...(user.featureAccess === undefined ? {} : { featureAccess: sanitizeFeatureAccess(user.featureAccess) }),
  };
}

/** 店铺 id 数组清洗：非字符串 / 空串剔除，去重（保持稳定顺序）。 */
export function sanitizeShopIds(raw: unknown): readonly string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const id = item.trim();
    if (id === "" || out.includes(id)) continue;
    out.push(id);
  }
  return out;
}

/**
 * 功能权限清洗：只保留**合法档位**（`read` / `write`）且**已登记**的功能点。
 * 非法值一律丢弃（契约 `accessOf` 也会 fail-closed，这里只是不把垃圾带进渲染层）。
 */
export function sanitizeFeatureGrants(raw: unknown): FeatureGrants {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Partial<Record<FeatureKey, AccessLevel>> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (featurePointOf(key) === undefined) continue;
    if (value !== "read" && value !== "write") continue;
    out[key as FeatureKey] = value;
  }
  return out;
}

// ---------------------------------------------------------------------------
// 三个判定（导航 / 路由守卫 / 写控件共用）
// ---------------------------------------------------------------------------

/**
 * 判定主体：只用到**服务端的有效档位** `featureAccess`（`/v1/auth/me` 下发）。
 * `role` / `features` 仍可存在（管理链、覆盖编辑器用），但**不参与功能权限判定**。
 */
export interface PermissionSubject {
  readonly role?: string | undefined;
  /** ⚠️ 不参与功能判定（管理链/覆盖编辑器用）⇒ 形状放宽，避免无关类型摩擦。 */
  readonly features?: Readonly<Partial<Record<string, "read" | "write" | "none">>> | undefined;
  readonly featureAccess?:
    | readonly {
        readonly feature: string;
        readonly access: "none" | "read" | "write";
        readonly source?: "override" | "role" | "super_admin" | undefined;
      }[]
    | undefined;
}

/**
 * ⭐⭐ 有效档位查询 —— **唯一依据 = 服务端 `featureAccess`**（`source` 也照用，含 `super_admin`）。
 *
 * 背景（2026-10-08 线上报障）：此前前端读的是 **`features`（用户覆盖）** ⇒
 * **有角色模板但零覆盖**的用户（如 `manager` + 18 条 grants + 0 overrides）被判成"啥都没有"。
 * ⇒ 现在：**`read` ⇒ 可看；`write` ⇒ 可写可看；`none` / 不在列表 / 整个 `featureAccess` 缺失 ⇒ 不可用**
 * （**fail closed**：缺失 = 不可用，⛔ **不回落** `features` —— 回落就是让两套口径并存，正是本次 bug 的成因）。
 */
export function featureAccessLevel(subject: PermissionSubject | null, key: string): "none" | "read" | "write" {
  if (subject === null) return "none";
  const entries = subject.featureAccess;
  if (entries === undefined) return "none";
  const row = entries.find((entry) => entry.feature === key);
  return row === undefined ? "none" : row.access;
}

/** 能不能看到该**页面**（未登记页面 ⇒ `true`，见 `PAGE_FEATURES` 说明）。 */
export function canViewPage(user: PermissionSubject | null, path: string): boolean {
  if (user === null) return false;
  const feature = featureForPage(path);
  if (feature === null) return true;
  return canViewFeature(user, feature);
}

/** 能不能在该页面**写入**（未登记 / 只读功能点 ⇒ false）。 */
export function canWritePage(user: PermissionSubject | null, path: string): boolean {
  if (user === null) return false;
  const feature = featureForPage(path);
  if (feature === null) return false;
  return canWriteFeature(user, feature);
}

/** 该用户能不能看某个功能点（导航 / 面板入口用）：`read` 或 `write` ⇒ 可看。 */
export function canViewFeature(user: PermissionSubject | null, key: string): boolean {
  const level = featureAccessLevel(user, key);
  return level === "read" || level === "write";
}

/**
 * 该用户能不能在某个功能点写入：**服务端 `access === "write"`** 且该功能点**契约上可写**
 * （`featurePointOf(key).writable` —— ⚠️ 这不是"第二套口径"：它是**同一契约**里
 * "只读功能点不存在 write 档"的守卫，与服务端 `effectiveAccess` 同源；服务端本就不会发 `write`）。
 */
export function canWriteFeature(user: PermissionSubject | null, key: string): boolean {
  if (featureAccessLevel(user, key) !== "write") return false;
  return featurePointOf(key)?.writable === true;
}

// ---------------------------------------------------------------------------
// 统一文案（403 / 禁用原因 / 空态）
// ---------------------------------------------------------------------------

/** 写控件禁用时的 hover 说明（只读权限）。 */
export const READ_ONLY_TOOLTIP = "只读权限：你的账号对该功能只有查看权限，写操作已禁用。";

/**
 * 「权限不足」统一文案（**不显示原始错误码**；原码只进 tooltip 供排障）。
 */
export function permissionDeniedMessage(featureKeyOrPage: string, options?: { readonly isPage?: boolean }): string {
  const label = options?.isPage === true ? (featureLabelForPage(featureKeyOrPage) ?? featureKeyOrPage) : featureLabel(featureKeyOrPage);
  return `权限不足：${label}。请联系管理员为你的账号开通该功能（或只读权限）。`;
}

/**
 * **页面内写控件的统一入参**（纯核；hook `usePageWriteAccess` 只做接线）。
 *
 * 页面据此把写按钮设成 `disabled={!access.canWrite}` + `title={access.readOnlyTooltip}`：
 *  · `canWrite=false` ⇒ 按钮**存在但 disabled**（**不是移除**——移除会让用户以为"功能没了"）；
 *  · `readOnlyTooltip` 固定为 `READ_ONLY_TOOLTIP`（hover 说明「只读权限」）；
 *  · **只决定能不能写，绝不隐藏数据**（可见性由 `canView` / 路由守卫 / 服务端决定）；
 *  · ⚠️ 前端禁用只是**体验**：真正的拒绝在服务端（直接打 API 仍 403）。
 */
export interface PageWriteAccess {
  readonly feature: string;
  readonly canView: boolean;
  readonly canWrite: boolean;
  readonly readOnlyTooltip: string | null;
}

/** 纯核：会话 →「这页能不能写」（可单测，无 React；hook 只做接线）。 */
export function pageWriteAccess(
  user: Pick<UserAccess, "role" | "features"> | null,
  feature: string,
): PageWriteAccess {
  const canWriteFeatureNow = canWriteFeature(user, feature);
  return {
    feature,
    canView: canViewFeature(user, feature),
    canWrite: canWriteFeatureNow,
    readOnlyTooltip: canWriteFeatureNow ? null : READ_ONLY_TOOLTIP,
  };
}

/** 管理员面板用的档位中文名（三档选择器）。 */
export const ACCESS_LEVEL_LABEL: Readonly<Record<AccessLevel, string>> = {
  none: "无权限",
  read: "只读",
  write: "写入",
};

/** 某功能点可选的档位：`writable:false` 的功能点**只给「无 / 只读」**。 */
export function selectableAccessLevels(key: string): readonly AccessLevel[] {
  const point = featurePointOf(key);
  if (point === undefined) return ["none"];
  return point.writable ? ["none", "read", "write"] : ["none", "read"];
}

/** 按模块分组的渲染清单（唯一来源仍是契约 `FEATURE_POINTS`，这里只分组、不新增）。 */
export function featurePointsByModule(): readonly { readonly module: string; readonly points: readonly (typeof FEATURE_POINTS)[number][] }[] {
  const order: string[] = [];
  const buckets = new Map<string, (typeof FEATURE_POINTS)[number][]>();
  for (const point of FEATURE_POINTS) {
    if (!buckets.has(point.module)) {
      buckets.set(point.module, []);
      order.push(point.module);
    }
    buckets.get(point.module)?.push(point);
  }
  return order.map((module) => ({ module, points: buckets.get(module) ?? [] }));
}

/** 功能档位摘要（用户列表里「功能档位摘要」一栏）。 */
export function featureSummary(features: FeatureGrants): string {
  const parts: string[] = [];
  for (const point of FEATURE_POINTS) {
    const level = features[point.key];
    if (level === undefined || level === "none") continue;
    parts.push(`${point.label}:${ACCESS_LEVEL_LABEL[level]}`);
  }
  return parts.length === 0 ? "无功能权限" : parts.join(" · ");
}

/** 超管标识（面板里给「超管不受限」提示用）。 */
export function isAdmin(user: Pick<UserAccess, "role"> | null): boolean {
  return user !== null && isSuperAdmin(user);
}

// ---------------------------------------------------------------------------
// 店铺选择器（C1 Phase 3）
// ---------------------------------------------------------------------------

/** 「全部店铺」哨兵（与 `lib/globalContext.ts` 的 `ALL_SHOPS` 同值；此处避免跨模块循环依赖）。 */
const ALL_SHOPS_SENTINEL = "";

/**
 * 权限变更后，当前店铺若已**不可见** → 返回应自动切到的店铺 id（**第一个可见店铺**）；
 * 无需切换 ⇒ `null`。
 *
 * 规则（设计稿 §3.2-1）：
 *  · 当前是「全部店铺」→ 不切（聚合视图仍合法，服务端会按可见集合裁剪交叉聚合）；
 *  · 当前店仍在可见集合里 → 不切；
 *  · 否则优先「当前品牌下第一个可见店」，再退「整体第一个可见店」；一个都没有 ⇒ `null`
 *    （由 UI 显示「你没有任何店铺权限」）。
 *
 * 纯函数：可见集合来自**服务端** `GET /v1/shops`（已按权限裁剪），这里不推断权限。
 */
export function pickShopAfterPermissionChange(
  visibleShopIds: readonly string[],
  brandScopedShopIds: readonly string[],
  currentShopId: string,
): string | null {
  if (currentShopId === ALL_SHOPS_SENTINEL) return null;
  if (visibleShopIds.includes(currentShopId)) return null;
  const fallback = brandScopedShopIds[0] ?? visibleShopIds[0];
  if (fallback === undefined || fallback === currentShopId) return null;
  return fallback;
}
